import { AsyncPipe, DatePipe, LowerCasePipe, NgClass } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  InjectionToken,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Signal,
  ViewChild,
  afterNextRender,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatOption } from '@angular/material/autocomplete';
import { MatError } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import {
  AbstractArTran,
  ArTranType,
  BankApiService,
  BaseBank,
  BaseCreateArTranPayload,
  ClientApiService,
  ClientEntity,
  CreateArTranPayload,
  CreateLabelledArTranPayload,
  CreatePaymentPayload,
  CreditArTranType,
  InvoiceArTran,
  PAYMENT_METHOD_TYPE_LABEL,
  PaymentMethodType,
} from '@anduin/api';
import {
  DEFAULT_LOADABLE_STATE,
  DateConstants,
  ExtendedCurrencyPipe,
  Loadable,
  LoadableStatus,
  Logger,
  SimpleSelectOption,
  Validate,
  canShowValidationErrors,
  compareStringsIgnoreCase,
  extractFormattedNumber,
  getControlValueChanges,
  isLoaded,
  mapToLoadable,
} from '@anduin/shared';
import { Store } from '@ngxs/store';
import { DpDatePickerModule, IDatePickerDirectiveConfig } from 'ng2-date-picker';
import { TOpens } from 'ng2-date-picker/lib/common/types/poistions.type';
import { BehaviorSubject, Observable, Subject, combineLatest, merge, of } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, skip, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ArTranApiService } from 'src/app/api/services/ar-tran-api.service';
import { InputErrorMessageDirective } from 'src/app/shared/directives/input-error-message/input-error-message.directive';
import { AutocompleteComponent } from '../../../../foundations/components/autocomplete/autocomplete.component';
import { DatePatternDirective } from '../../../../shared/directives/date-pattern.directive';
import { NumberOnlyDirective } from '../../../../shared/directives/number-only.directive';
import { NewTransactionType } from '../../models/transactions-ui.models';
import { ArManagementState, SetAreTransactionSidebarChangesPending } from '../../state';
import { TransactionAllocationsComponent } from '../transaction-allocations/transaction-allocations.component';
import { toIsoDate } from '../transactions-table/transactions-table.utils';

/** Injection token to signal that NewTransactionComponent is running in dialog mode (outside AR Management context). */
export const NEW_TRANSACTION_DIALOG_MODE = new InjectionToken<boolean>('NEW_TRANSACTION_DIALOG_MODE');

@Component({
  selector: 'app-new-transaction',
  templateUrl: './new-transaction.component.html',
  styleUrls: ['./new-transaction.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    AutocompleteComponent,
    InputErrorMessageDirective,
    MatSelect,
    MatOption,
    DatePatternDirective,
    DpDatePickerModule,
    NgClass,
    MatError,
    NumberOnlyDirective,
    TransactionAllocationsComponent,
    AsyncPipe,
    LowerCasePipe,
  ],
})
export class NewTransactionComponent implements Validate, OnInit, OnDestroy {
  /** When true, component operates independently of NGXS store (for use in dialogs). */
  @Input() dialogMode = inject(NEW_TRANSACTION_DIALOG_MODE, { optional: true }) ?? false;
  /** Override transaction type (used in dialog mode instead of store selector). */
  @Input() overrideTransactionType: CreditArTranType | undefined;
  /** Override client ID — hides client field and sets client form control. */
  @Input() overrideClientId: number | undefined;
  /** Pre-fill the total amount field. */
  @Input() prefilledAmount: number | undefined;
  /** When set, auto-allocates the matching invoice after linkable transactions load. */
  @Input() preselectedInvoiceNumber: string | undefined;

  @ViewChild('paymentMethodField') paymentMethodField: MatSelect;
  @ViewChild('notesField') notesField: ElementRef<HTMLElement>;

  readonly clientFieldElement = viewChild<ElementRef>('clientField');

  get today(): string {
    return this.datePipe.transform(new Date(), 'MM/dd/yyyy');
  }

  readonly LoadableStatus = LoadableStatus;
  private readonly _unsubscribe$ = new Subject<void>();
  private hasAutoAllocated = false;

  @Output() newTransactionChange = new EventEmitter<CreateArTranPayload>();

  private selectedNewTransactionType$: Observable<NewTransactionType> = this.dialogMode
    ? of(undefined)
    : this.store.select(ArManagementState.getSelectedNewTransactionOptionType).pipe(shareReplay(1));

  /** Whether client field should be hidden (dialog mode with overrideClientId, or single-client scope). */
  readonly hideClientField = signal(false);

  isNewTransactionTypePayment: Signal<boolean> = this.dialogMode
    ? signal(false)
    : toSignal(this.selectedNewTransactionType$.pipe(map((transactionType) => transactionType === CreditArTranType.RECEIPT)), {
        initialValue: false,
      });

  // Computed signals for error messages
  clientErrors = computed(() => ({
    required: 'Client is required',
  }));

  bankErrors = computed(() => ({
    required: 'Bank is required',
  }));

  referenceErrors = computed(() => ({
    required: 'Reference is required',
  }));

  notesErrors = computed(() => ({
    required: 'Note is required',
    maxlength: 'Note cannot be longer than 300 characters',
  }));

  totalErrors = computed(() => ({
    required: 'Total is required',
    invalidAmount: 'Amount must be greater than zero',
  }));

  transactionDateErrors = computed(() => ({
    required: 'Transaction date is required',
  }));

  depositDateErrors = computed(() => ({
    required: 'Deposit date is required',
  }));

  transactionDisplay = this.dialogMode ? '' : this.store.selectSnapshot(ArManagementState.getSelectedNewTransactionOptionDisplay);
  transactionForm = this.getBaseTransactionForm();
  getBaseTransactionForm(): UntypedFormGroup {
    return new UntypedFormGroup({
      client: new UntypedFormControl(undefined, [Validators.required]),
      notes: new UntypedFormControl(undefined, [Validators.maxLength(300)]), // TODO: Is this optional?
      total: new UntypedFormControl(undefined, [
        Validators.required,
        (control: UntypedFormControl) => (control.value && extractFormattedNumber(control.value) <= 0 ? { invalidAmount: true } : null),
      ]),
      transactionDate: new UntypedFormControl(this.today, [Validators.required]),
    });
  }

  clientSearchFn$ = (searchTerm: string) =>
    this.clientApiService
      .getClientsBySearchTerm(searchTerm)
      .pipe(
        map((clients) =>
          clients.sort((clientA: ClientEntity, clientB: ClientEntity) => compareStringsIgnoreCase(clientA.name, clientB.name)),
        ),
      );
  bankSearchFn$ = (searchTerm: string) =>
    this.bankApiService
      .getBanksBySearchTerm(searchTerm)
      .pipe(map((banks) => banks.sort((bankA: BaseBank, bankB: BaseBank) => compareStringsIgnoreCase(bankA.name, bankB.name))));

  isOnlyOneClientInScope$ = this.dialogMode
    ? of(true)
    : this.store.select(ArManagementState.getContextStatus).pipe(
        isLoaded(true),
        map((status) => {
          if (status === LoadableStatus.ERROR) {
            return false;
          }

          const clientIdsScope = this.store.selectSnapshot(ArManagementState.getClientIdsScope);
          const isOnlyOneClientInScope = (clientIdsScope ?? []).length === 1;

          if (isOnlyOneClientInScope) {
            this.transactionForm.controls.client.setValue({ id: clientIdsScope[0] });
            this.hideClientField.set(true);
          }

          return isOnlyOneClientInScope;
        }),
      );

  paymentMethodTypeOptions: SimpleSelectOption<PaymentMethodType>[] = [
    { value: PaymentMethodType.CHECK, display: PAYMENT_METHOD_TYPE_LABEL[PaymentMethodType.CHECK] },
    { value: PaymentMethodType.ACH, display: PAYMENT_METHOD_TYPE_LABEL[PaymentMethodType.ACH] },
  ];

  transactionDatePickerConfig = this.getTransactionDatePickerConfig('left');
  depositDatePickerConfig = this.getTransactionDatePickerConfig('right');
  getTransactionDatePickerConfig(opens: TOpens): IDatePickerDirectiveConfig {
    return {
      ...DateConstants.DEFAULT_DATE_CONFIG,
      max: this.datePipe.transform(new Date(), 'MM/dd/yyyy'),
      opens,
    };
  }

  private _resetOnNewTransactionTypeChange$ = this.selectedNewTransactionType$.pipe(
    distinctUntilChanged(),
    tap((newTransactionType) => {
      if (newTransactionType === CreditArTranType.RECEIPT) {
        this.buildPaymentForm();
      } else if (this.transactionForm.get('paymentMethodType')) {
        this.destroyPaymentForm();
      }
    }),
    skip(1),
    tap((_) => this.reset()),
    takeUntil(this._unsubscribe$),
  );

  pendingTransactionLinks$ = new BehaviorSubject<Record<string, number>>(undefined);
  onTransactionLinksChange(pendingTransactionLinks: Record<string, number>): void {
    this.pendingTransactionLinks$.next(pendingTransactionLinks);
  }

  newArTran$: Observable<CreateArTranPayload> = combineLatest([
    getControlValueChanges<Record<string, unknown>>(this.transactionForm),
    this.pendingTransactionLinks$,
  ]).pipe(
    map(([transactionFormValue, pendingTransactionLinks]) => {
      const baseTransactionFormValue: BaseCreateArTranPayload = {
        clientId: (transactionFormValue.client as { id: number })?.id,
        firmBankAccountId: (transactionFormValue.bank as { id: number })?.id,
        notes: transactionFormValue.notes as string,
        amount: transactionFormValue.total ? extractFormattedNumber(transactionFormValue.total as string) : undefined,
        arType: this.overrideTransactionType ?? this.store.selectSnapshot(ArManagementState.getSelectedNewTransactionOptionType),
        allocations: Object.entries(pendingTransactionLinks ?? {}).map(([arTranId, allocated]) => ({
          arTranId: Number(arTranId),
          allocated,
        })),
      };

      if (baseTransactionFormValue.arType === CreditArTranType.RECEIPT) {
        const transactionDateString = transactionFormValue.transactionDate as string;
        const depositDateString = transactionFormValue.depositDate as string;
        return {
          ...baseTransactionFormValue,
          // TODO: Remove fallback once other payment method types are implemented (waiting on backend)
          paymentMethodType: (transactionFormValue.paymentMethodType as PaymentMethodType) || PaymentMethodType.CHECK,
          reference: transactionFormValue.reference as string,
          transactionDate: toIsoDate(transactionDateString) ?? undefined,
          depositDate: toIsoDate(depositDateString) ?? undefined,
        } as CreatePaymentPayload;
      }

      const selectedNewTransactionOptionLabel = this.dialogMode
        ? undefined
        : this.store.selectSnapshot(ArManagementState.getSelectedNewTransactionOption)?.labelId;
      if (selectedNewTransactionOptionLabel != undefined) {
        const transactionDateString = transactionFormValue.transactionDate as string;
        // CreateAR parses a LocalDateTime and rejects a bare date with DateTimeParseException.
        // Local midnight with no zone keeps the no-UTC-shift guarantee this conversion is for.
        const transactionDateDay = toIsoDate(transactionDateString);
        const transactionDate = transactionDateDay ? `${transactionDateDay}T00:00:00` : undefined;
        return {
          ...baseTransactionFormValue,
          label: selectedNewTransactionOptionLabel,
          transactionDate,
        } as CreateLabelledArTranPayload;
      }

      return baseTransactionFormValue;
    }),
    tap((newArTran) => this.newTransactionChange.emit(newArTran)),
  );

  linkableArTrans$ = getControlValueChanges<ClientEntity>(this.transactionForm.get('client')).pipe(
    switchMap((selectedClient) => {
      if (selectedClient == undefined) {
        return of(DEFAULT_LOADABLE_STATE as Loadable<AbstractArTran[]>);
      }

      const newTransactionType =
        this.overrideTransactionType ?? this.store.selectSnapshot(ArManagementState.getSelectedNewTransactionOptionType);
      const linkableTypeOptions = AbstractArTran.getLinkableTypes(newTransactionType as ArTranType);

      return this.arTranApiService.getLinkableArTransForClient(selectedClient.id, linkableTypeOptions).pipe(
        map((arTrans) => arTrans.map(AbstractArTran.create).filter(Boolean)),
        map((arTrans) => arTrans.sort((a, b) => b.amountRemaining - a.amountRemaining)),
        mapToLoadable(),
      );
    }),
    tap((linkableArTrans) => {
      if (linkableArTrans.status === LoadableStatus.ERROR) {
        this.logger.error(
          'NewTransactionComponent :: _manageLinkableArTrans$ :: Failed to get default AR transaction link options. Error: ' +
            JSON.stringify(linkableArTrans.error),
        );
      }

      if (this.preselectedInvoiceNumber && linkableArTrans.status === LoadableStatus.LOADED && !this.hasAutoAllocated) {
        this.hasAutoAllocated = true;
        afterNextRender(
          () => {
            const matchingArTran = linkableArTrans.data.find(
              (arTran) => arTran instanceof InvoiceArTran && arTran.invoiceNumber === this.preselectedInvoiceNumber,
            );
            if (matchingArTran) {
              this.transactionAllocationsComponent.onBodyCheckboxClicked(false, matchingArTran);
            }
          },
          { injector: this.injector },
        );
      }
    }),
  );

  constructor(
    private readonly clientApiService: ClientApiService,
    private readonly bankApiService: BankApiService,
    private readonly store: Store,
    private readonly currencyPipe: ExtendedCurrencyPipe,
    private readonly datePipe: DatePipe,
    private readonly arTranApiService: ArTranApiService,
    private readonly logger: Logger,
    private readonly injector: Injector,
  ) {
    if (!this.dialogMode) {
      this._resetOnNewTransactionTypeChange$.subscribe();

      const transactionType = this.store.selectSnapshot(ArManagementState.getSelectedNewTransactionOptionType);
      if (transactionType === CreditArTranType.RECEIPT) {
        this.buildPaymentForm();
      }
    }
  }

  ngOnInit(): void {
    // Only track sidebar dirty state in sidebar context (not in dialog mode)
    if (!this.dialogMode) {
      this._manageAreSidebarChangesPending$.subscribe();
    }

    if (!this.dialogMode) {
      return;
    }

    // In dialog mode, override the payment type signal
    if (this.overrideTransactionType === CreditArTranType.RECEIPT) {
      this.isNewTransactionTypePayment = signal(true);
      if (!this.transactionForm.get('paymentMethodType')) {
        this.buildPaymentForm();
      }
    }

    // Set the display label
    if (this.overrideTransactionType) {
      this.transactionDisplay = this.overrideTransactionType === CreditArTranType.RECEIPT ? 'Payment' : this.transactionDisplay;
    }

    // Pre-fill client
    if (this.overrideClientId != null) {
      this.transactionForm.controls.client.setValue({ id: this.overrideClientId });
      this.hideClientField.set(true);
    }

    // Pre-fill amount
    if (this.prefilledAmount != null) {
      this.transactionForm.controls.total.setValue(this.formatMonetaryAmount(String(this.prefilledAmount)));
    }
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  buildPaymentForm(): void {
    this.transactionForm.addControl(
      'paymentMethodType',
      new UntypedFormControl(this.paymentMethodTypeOptions[0].value, [Validators.required]),
    );
    this.transactionForm.addControl('bank', new UntypedFormControl(undefined, [Validators.required]));
    this.transactionForm.addControl('reference', new UntypedFormControl(undefined, [Validators.required]));
    this.transactionForm.addControl('depositDate', new UntypedFormControl(this.today, [Validators.required]));

    this.transactionForm.addValidators((group: UntypedFormGroup) => {
      // return validation error if transaction date is after deposit date
      const transactionDate = group.get('transactionDate').value;
      const depositDate = group.get('depositDate').value;
      return transactionDate && depositDate && new Date(transactionDate) > new Date(depositDate)
        ? { depositDateBeforeTransactionDate: true }
        : null;
    });
  }

  destroyPaymentForm(): void {
    this.transactionForm.clearValidators();
    this.transactionForm.removeControl('paymentMethodType');
    this.transactionForm.removeControl('bank');
    this.transactionForm.removeControl('reference');
    this.transactionForm.removeControl('depositDate');
  }

  formatTotalControlValue(): void {
    const totalControl = this.transactionForm.get('total');
    const formattedTotal = this.formatMonetaryAmount(totalControl.value);
    totalControl.setValue(formattedTotal);
  }

  formatMonetaryAmount(amount: string): string {
    const amountAsNumber = extractFormattedNumber(amount);
    const finalAmount = Number.isNaN(amountAsNumber) ? 0 : amountAsNumber;
    return this.currencyPipe.transform(finalAmount, undefined, '');
  }

  shouldShowValidationErrors(controlName: string, validationErrorKey?: string): boolean {
    const control = this.transactionForm.get(controlName);
    return control.errors && canShowValidationErrors(control) && (!validationErrorKey || control.hasError(validationErrorKey));
  }

  private _manageAreSidebarChangesPending$ = merge(this.transactionForm.valueChanges, this.pendingTransactionLinks$).pipe(
    map((_) => this.isPristine()),
    distinctUntilChanged(),
    switchMap((isPristine) => this.store.dispatch(new SetAreTransactionSidebarChangesPending(!isPristine))),
    takeUntil(this._unsubscribe$),
  );

  isPristine(): boolean {
    return this.transactionForm.pristine;
  }

  @ViewChild(TransactionAllocationsComponent, { static: true }) transactionAllocationsComponent: TransactionAllocationsComponent;
  validate(): Observable<boolean> {
    this.transactionForm.markAllAsTouched();

    return this.transactionAllocationsComponent.validate().pipe(map((isValid) => isValid && this.transactionForm.valid));
  }

  reset(isSaveAndNew = false): void {
    const transactionType =
      this.overrideTransactionType ?? this.store.selectSnapshot(ArManagementState.getSelectedNewTransactionOptionType);

    // #region Previous Form Values
    const previousBank = this.transactionForm.value.bank;
    const previousClient = this.transactionForm.value.client;
    const previousTransactionDate = this.transactionForm.value.transactionDate;
    const previousDepositDate = this.transactionForm.value.depositDate;
    //#endregion

    this.transactionForm.reset({
      ...(transactionType === CreditArTranType.RECEIPT ? { paymentMethodType: this.transactionForm.value.paymentMethodType } : {}),
      client: isSaveAndNew ? previousClient : undefined,
      transactionDate: isSaveAndNew ? previousTransactionDate : this.today,
      depositDate: isSaveAndNew ? previousDepositDate : undefined,
      bank: isSaveAndNew ? previousBank : undefined,
    });

    // confused why this is needed when the documentation for reset() says it should reset the form to its original state
    this.transactionForm.markAsUntouched();
    this.transactionForm.markAsPristine();

    this.transactionAllocationsComponent.reset();

    if (!this.clientFieldElement()) {
      if (transactionType === CreditArTranType.RECEIPT) {
        this.paymentMethodField.focus();
        return;
      }

      this.notesField.nativeElement.focus();
    }
  }
}
